// ===============================================================
// POP-UP & PUSH BİLDİRİM SERVİSİ (Firestore notifications Koleksiyonu & LocalStorage)
// ===============================================================
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  arrayUnion,
  serverTimestamp
} from "firebase/firestore";

const LOCAL_POPUPS_KEY = 'kocum_popup_notifications';

/**
 * Yerel depolamadan pop-up listesini alır
 */
export function getLocalPopups() {
  try {
    const raw = localStorage.getItem(LOCAL_POPUPS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.error("Yerel pop-up'lar okunamadı:", e);
    return [];
  }
}

/**
 * Yerel depolamaya pop-up listesini yazar
 */
export function saveLocalPopups(popups) {
  try {
    localStorage.setItem(LOCAL_POPUPS_KEY, JSON.stringify(popups));
  } catch (e) {
    console.error("Yerel pop-up'lar kaydedilemedi:", e);
  }
}

/**
 * Pop-up ve Push bildirimlerini Firestore "notifications" koleksiyonundan gerçek zamanlı dinler
 */
export function subscribePopups(db, isFirebaseActive, onUpdate) {
  if (isFirebaseActive && db) {
    try {
      const q = query(collection(db, "notifications"));
      const unsub = onSnapshot(q, (snapshot) => {
        const list = snapshot.docs
          .map(d => {
            const data = d.data();
            if (!data.isPopupNotification) return null;

            let createdAtStr = new Date().toISOString();
            if (data.createdAt && typeof data.createdAt.toDate === 'function') {
              createdAtStr = data.createdAt.toDate().toISOString();
            } else if (data.createdAt) {
              createdAtStr = data.createdAt;
            } else if (data.date) {
              createdAtStr = data.date;
            }

            return {
              id: d.id,
              ...data,
              createdAt: createdAtStr,
              dismissedBy: Array.isArray(data.dismissedBy) ? data.dismissedBy : []
            };
          })
          .filter(Boolean)
          .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

        // Yerel önbellekle birleştir
        const localList = getLocalPopups();
        const map = new Map();
        localList.forEach(p => map.set(p.id, p));
        list.forEach(p => map.set(p.id, p));
        const combined = Array.from(map.values()).sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

        saveLocalPopups(combined);
        onUpdate(combined);
      }, (error) => {
        console.warn("Firestore notifications onSnapshot hatası, yerel veriye dönülüyor:", error);
        onUpdate(getLocalPopups());
      });
      return unsub;
    } catch (err) {
      console.warn("Firestore notifications sorgu hatası:", err);
    }
  }

  // Yerel veri fallback
  onUpdate(getLocalPopups());
  const handleStorageChange = (e) => {
    if (e.key === LOCAL_POPUPS_KEY) {
      onUpdate(getLocalPopups());
    }
  };
  window.addEventListener('storage', handleStorageChange);
  return () => window.removeEventListener('storage', handleStorageChange);
}

/**
 * Yeni bir pop-up veya push bildirimi oluşturur (Firestore notifications koleksiyonuna yazar)
 */
export async function createPopup({
  title,
  message,
  type = 'info', // 'info' | 'warning' | 'urgent' | 'success'
  deliveryChannel = 'both', // 'both' | 'popup' | 'push'
  targetType = 'all', // 'all' | 'students' | 'teachers' | 'custom'
  targetUserIds = [],
  targetUserNames = [],
  buttonText = 'Tamam, Anladım',
  actionLink = '',
  actionLinkText = '',
  authorName = 'Sistem Yöneticisi',
  db = null,
  isFirebaseActive = false
}) {
  const nowIso = new Date().toISOString();
  const newPopup = {
    isPopupNotification: true,
    title: title.trim(),
    message: message.trim(),
    text: message.trim(), // notifications koleksiyonu standart uyumluluğu için
    type,
    deliveryChannel,
    targetType,
    target: targetType,
    targetUserIds: Array.isArray(targetUserIds) ? targetUserIds : [],
    targetUserNames: Array.isArray(targetUserNames) ? targetUserNames : [],
    buttonText: buttonText.trim() || 'Tamam, Anladım',
    actionLink: (actionLink || '').trim(),
    actionLinkText: (actionLinkText || '').trim(),
    authorName,
    sender: authorName,
    isActive: true,
    dismissedBy: [],
    date: nowIso,
    createdAt: nowIso
  };

  if (isFirebaseActive && db) {
    try {
      const docRef = await addDoc(collection(db, "notifications"), {
        ...newPopup,
        createdAt: serverTimestamp()
      });
      const created = { id: docRef.id, ...newPopup };
      const localList = getLocalPopups();
      localList.unshift(created);
      saveLocalPopups(localList);
      return created;
    } catch (err) {
      console.error("Firestore notification ekleme hatası:", err);
    }
  }

  // Yerel kayıt fallback
  const localList = getLocalPopups();
  const createdWithId = {
    id: `popup_local_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
    ...newPopup
  };
  localList.unshift(createdWithId);
  saveLocalPopups(localList);
  return createdWithId;
}

/**
 * Bildirim durumunu açar/kapatır (Aktif / Pasif)
 */
export async function togglePopupStatus(popupId, newStatus, db, isFirebaseActive) {
  if (isFirebaseActive && db && !popupId.startsWith('popup_local_')) {
    try {
      const ref = doc(db, "notifications", popupId);
      await updateDoc(ref, { isActive: newStatus });
    } catch (err) {
      console.error("Firestore bildirim durum güncelleme hatası:", err);
    }
  }

  const list = getLocalPopups();
  const index = list.findIndex(p => p.id === popupId);
  if (index !== -1) {
    list[index].isActive = newStatus;
    saveLocalPopups(list);
    return true;
  }
  return false;
}

/**
 * Bildirimi kalıcı olarak siler (Hem Firestore hem localStorage'dan)
 */
export async function deletePopup(popupId, db, isFirebaseActive) {
  // 1. Yerel listeden anında temizle
  const list = getLocalPopups();
  const filtered = list.filter(p => p.id !== popupId);
  saveLocalPopups(filtered);

  // 2. Firestore notifications koleksiyonundan sil
  if (isFirebaseActive && db) {
    try {
      const ref = doc(db, "notifications", popupId);
      await deleteDoc(ref);
    } catch (err) {
      console.warn("Firestore bildirim silme uyarısı:", err);
    }
  }

  return true;
}

/**
 * Kullanıcının bildirimi okuyup kapattığını kaydeder (dismiss)
 */
export async function markPopupDismissed(popupId, userId, db, isFirebaseActive) {
  if (!popupId || !userId) return;

  const uidStr = String(userId);

  // 1. Yerel önbelleğe anında yaz
  try {
    const key = `kocum_dismissed_popups_${userId}`;
    const raw = localStorage.getItem(key);
    const dismissedArr = raw ? JSON.parse(raw) : [];
    if (!dismissedArr.includes(popupId)) {
      dismissedArr.push(popupId);
      localStorage.setItem(key, JSON.stringify(dismissedArr));
    }
  } catch (e) {}

  // 2. Firestore'da dismissedBy güncelle
  if (isFirebaseActive && db && !popupId.startsWith('popup_local_')) {
    try {
      const ref = doc(db, "notifications", popupId);
      await updateDoc(ref, {
        dismissedBy: arrayUnion(uidStr)
      });
    } catch (err) {
      console.warn("Firestore popup dismissed update hatası:", err);
    }
  }

  // 3. Yerel listede de dismissedBy array'ine ekle
  const list = getLocalPopups();
  const index = list.findIndex(p => p.id === popupId);
  if (index !== -1) {
    if (!Array.isArray(list[index].dismissedBy)) {
      list[index].dismissedBy = [];
    }
    if (!list[index].dismissedBy.includes(uidStr)) {
      list[index].dismissedBy.push(uidStr);
      saveLocalPopups(list);
    }
  }
}

/**
 * Bildirim belirli bir kullanıcı için gösterilmeli mi kontrol eder
 */
export function shouldShowPopupForUser(popup, user) {
  if (!popup || !popup.isActive || !user || !user.id) return false;

  // Sadece 'push' seçilmişse uygulama içi modal açılmaz
  if (popup.deliveryChannel === 'push') {
    return false;
  }

  const uidStr = String(user.id);

  // Kullanıcı zaten kapatmış mı?
  if (Array.isArray(popup.dismissedBy) && popup.dismissedBy.some(id => String(id) === uidStr)) {
    return false;
  }

  try {
    const key = `kocum_dismissed_popups_${user.id}`;
    const raw = localStorage.getItem(key);
    if (raw) {
      const dismissedArr = JSON.parse(raw);
      if (dismissedArr.includes(popup.id)) {
        return false;
      }
    }
  } catch (e) {}

  // Hedef kontrolü
  if (popup.targetType === 'all') {
    return true;
  }

  if (popup.targetType === 'students') {
    return user.role === 'student';
  }

  if (popup.targetType === 'teachers') {
    return user.role === 'admin' || user.role === 'superadmin';
  }

  if (popup.targetType === 'custom') {
    if (Array.isArray(popup.targetUserIds)) {
      return popup.targetUserIds.some(id => String(id) === uidStr);
    }
    return false;
  }

  return false;
}
